import React, { useEffect, useState } from "react";
import { useProductUpdate } from "hooks";
import {
  Product,
  ProductColumnToUpdate,
  ProductUpdateInput,
  UpdateProductInput,
  ValueSubmitTrigger,
} from "types";
import { Loading } from "core";
import { TableNumberField } from "components";
import { getPriceCalc } from "modules/pricecalcs/api";

type ProductNumberColumnProps = {
  selectedColumn: ProductColumnToUpdate | null;
  selectedRow: ProductUpdateInput | null;
  isEditMode: boolean;
  product: ProductUpdateInput;
  productField: keyof Product;
  productColumn: ProductColumnToUpdate;
  rowIndex: number;
  handleProductSelection: (
    product: ProductUpdateInput,
    value: number,
    column: ProductColumnToUpdate,
    rowIndex: number,
    trigger: ValueSubmitTrigger,
  ) => void;
  autoFocus?: boolean;
  disabled?: boolean;
  decimalScale?: number;
  numberFormatOptions?: Intl.NumberFormatOptions;
};

export const ProductNumberColumn: React.FC<ProductNumberColumnProps> = ({
  selectedColumn,
  selectedRow,
  isEditMode,
  product,
  productField,
  productColumn,
  rowIndex,
  handleProductSelection,
  autoFocus,
  disabled,
  decimalScale,
  numberFormatOptions,
}) => {
  const [value, setValue] = useState<number | "">("");

  const [submitLoading, setSubmitLoading] = useState<boolean>(false);

  useEffect(() => {
    const defaultValue = product[productField] ?? "";
    setValue(typeof defaultValue === "number" ? defaultValue : "");
  }, [product, productField]);

  const updateMutation = useProductUpdate();

  if (selectedColumn !== productColumn || selectedRow?.id !== product.id) {
    return product[productField]?.toLocaleString("de-DE", numberFormatOptions);
  }

  const submitPrice = async (
    value: number | "",
    trigger: ValueSubmitTrigger,
  ) => {
    const submitValue = value === "" ? 0 : value;

    if (
      !isEditMode &&
      submitValue !== product[productField] &&
      trigger !== ValueSubmitTrigger.Escape
    ) {
      setSubmitLoading(true);
      const priceCalc = product.storePriceCalcID
        ? await getPriceCalc(product.storePriceCalcID)
        : null;

      const updateProductInput: UpdateProductInput = {
        id: product.id,
        expansionID: product.expansionID,
        [productField]: submitValue,
      };

      if (priceCalc && priceCalc.priceCalculationStore) {
        const samePrice = submitValue === priceCalc.priceCalculationStore.price;

        if (!samePrice) {
          updateProductInput.priceStoreSetManually = true;
        }
      } else {
        updateProductInput.priceStoreSetManually = true;
      }

      await updateMutation.mutateAsync(updateProductInput);
      setSubmitLoading(false);
    }

    handleProductSelection(
      product,
      submitValue,
      productColumn,
      rowIndex,
      trigger,
    );
  };

  return submitLoading ? (
    <Loading
      description={value.toLocaleString("de-DE", {
        minimumFractionDigits: decimalScale,
        maximumFractionDigits: decimalScale,
      })}
      size="20px"
      style={{ margin: 0 }}
    />
  ) : (
    <TableNumberField
      id={product.id}
      value={value}
      onValueChange={(value) => setValue(value)}
      submitValue={submitPrice}
      autoFocus={autoFocus}
      disabled={disabled}
      decimalScale={decimalScale}
      ref={
        productColumn === ProductColumnToUpdate.deStock
          ? product.deStockInputRef
          : productColumn === ProductColumnToUpdate.deTransfer
            ? product.deTransferInputRef
            : productColumn === ProductColumnToUpdate.enStock
              ? product.enStockInputRef
              : productColumn === ProductColumnToUpdate.enTransfer
                ? product.enTransferInputRef
                : product.priceStoreInputRef
      }
    />
  );
};
